import { useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography } from '@material-ui/core';
import AssessmentIcon from '@material-ui/icons/Assessment';
import OptimizationPipelinePopupTable from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopupTable';
import { PopupToolbarChip } from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopup';
import { getSoftStatusClass } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/colorUtils';
import { STATUS_BADGE_CLASS, STATUS_CELL_CLASS } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/constantUtils';

const numberFormatter = new Intl.NumberFormat('hu-HU', {
	useGrouping: true,
	maximumFractionDigits: 6
});

const SMALL_BADGE_CLASS = 'px-8 py-4 rounded-md text-10 leading-none font-semibold whitespace-nowrap';

const LABEL_DICTIONARY = {
	accurate: 'Accurate',
	ae: 'Absolute error',
	ape: 'Absolute percentage error',
	aspect_penalty: 'Aspect penalty',
	base_state: 'Base state',
	base_value: 'Base value',
	best_objective: 'Best objective',
	class: 'Class',
	completed_state: 'Completed state',
	details: 'Details',
	domain_inject: 'Domain inject',
	elapsed_time: 'Elapsed time',
	end_date_time: 'End date time',
	error_message: 'Error message',
	estimated_value: 'Estimated value',
	features: 'Features',
	input_data_length: 'Input data length',
	is_successful: 'Successful',
	last_ts: 'Last timestamp',
	mae: 'MAE',
	mae_all: 'MAE all',
	mape: 'MAPE',
	mape_all: 'MAPE all',
	max_value: 'Maximum',
	message: 'Message',
	min_value: 'Minimum',
	mse: 'MSE',
	mse_all: 'MSE all',
	nans_percentage_removed: 'NaN percentage removed',
	nans_removed: 'NaNs removed',
	net_value: 'Net value',
	optimum_state: 'Optimum state',
	optimum_value: 'Optimum value',
	optimizer_details: 'Optimizer details',
	optimizer_log_run_id: 'Optimizer log run ID',
	outlier_percentage_removed: 'Outlier percentage removed',
	outliers_removed: 'Outliers removed',
	output_values: 'Output values',
	processed_timestamps: 'Processed timestamps',
	se: 'Squared error',
	skipped_count: 'Skipped count',
	skipped_timestamps: 'Skipped timestamps',
	start_date_time: 'Start date time',
	state_inject: 'State inject',
	state_penalty: 'State penalty',
	step_count: 'Steps',
	timestamps_processed: 'Processed timestamps',
	total_epochs: 'Total epochs',
	total_penalty: 'Total penalty',
	total_steps: 'Total steps',
	transformer_penalty: 'Transformer penalty',
	true_value: 'True value',
	without_nans_length: 'Length without NaNs',
	without_outliers_length: 'Length without outliers',
	written_count: 'Written count',
	written_timestamps: 'Written timestamps'
};

const WORD_DICTIONARY = {
	ae: 'AE',
	ape: 'APE',
	api: 'API',
	csv: 'CSV',
	id: 'ID',
	mae: 'MAE',
	mape: 'MAPE',
	mse: 'MSE',
	nan: 'NaN',
	nans: 'NaNs',
	pi: 'PI',
	se: 'SE',
	ts: 'Timestamp',
	uom: 'UOM'
};

const topLevelSummaryKeys = [
	'finished',
	'elapsed_time',
	'total_epochs',
	'total_steps',
	'optimizer_log_run_id',
	'input_data_length',
	'without_nans_length',
	'nans_removed',
	'nans_percentage_removed',
	'without_outliers_length',
	'outliers_removed',
	'outlier_percentage_removed',
	'start_date_time',
	'end_date_time',
	'processed_timestamps',
	'timestamps_processed',
	'written_count',
	'skipped_count',
	'last_ts'
];

const bestObjectiveSummaryKeys = [
	'value',
	'net_value',
	'state_penalty',
	'aspect_penalty',
	'transformer_penalty',
	'total_penalty'
];

const complexBestObjectiveKeys = [
	'state',
	'state_inject',
	'domain_inject',
	'aspect',
	'transformer'
];

function isObject(value) {
	return value !== null && typeof value === 'object';
}

function isPrimitive(value) {
	return value === null || value === undefined || typeof value !== 'object';
}

function isEmptyObject(value) {
	return !isObject(value) || Object.keys(value).length === 0;
}

function isDateKey(value) {
	return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value);
}

function isTimestampString(value) {
	return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value);
}

function formatTimestamp(value) {
	if (!isTimestampString(value)) return value;

	const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
	if (!match) return value;

	return `${match[1]}.${match[2]}.${match[3]}. ${match[4]}:${match[5]}:${match[6] || '00'}`;
}

function getTimestampSortValue(value) {
	if (!isTimestampString(value)) return null;

	const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
	if (!match) return null;

	return Number(`${match[1]}${match[2]}${match[3]}${match[4]}${match[5]}${match[6] || '00'}`);
}

function sortKeys(keys) {
	return [...keys].sort((a, b) => String(a).localeCompare(String(b)));
}

function countObjectKeys(value) {
	return isObject(value) ? Object.keys(value).length : 0;
}

function toTitleWord(value) {
	const lower = String(value || '').toLowerCase();
	if (WORD_DICTIONARY[lower]) return WORD_DICTIONARY[lower];
	return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function inferSegmentName(segment) {
	if (!segment) return '';
	if (LABEL_DICTIONARY[segment]) return LABEL_DICTIONARY[segment];
	if (!segment.includes('_') && /^[A-Z0-9+-]+$/.test(segment)) return segment;

	return segment
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.split(/[_\s]+/)
		.filter(Boolean)
		.map(toTitleWord)
		.join(' ');
}

function inferName(key) {
	if (key === null || key === undefined || key === '') return '-';

	const raw = String(key);
	if (LABEL_DICTIONARY[raw]) return LABEL_DICTIONARY[raw];
	if (isDateKey(raw)) return 'Date';
	if (!raw.includes('_') && !raw.includes('.') && !/[a-z0-9][A-Z]/.test(raw)) {
		if (/^[A-Z0-9+_.-]+$/.test(raw)) return raw;
		return toTitleWord(raw);
	}

	return raw
		.split('.')
		.map(inferSegmentName)
		.join(' / ');
}

function withLabel(row, keyName = 'key') {
	const technicalName = row[keyName] === null || row[keyName] === undefined ? '' : String(row[keyName]);
	const label = row.label || inferName(technicalName);

	return {
		...row,
		label,
		technicalName: technicalName && technicalName !== label ? technicalName : ''
	};
}

function formatNumber(value) {
	if (!Number.isFinite(value)) return String(value);
	return numberFormatter.format(value);
}

function formatValue(value) {
	if (value === null || value === undefined || value === '') return '-';
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	if (typeof value === 'number') return formatNumber(value);
	if (Array.isArray(value)) return value.length + ' item' + (value.length === 1 ? '' : 's');
	if (isObject(value)) return JSON.stringify(value);
	if (isTimestampString(value)) return formatTimestamp(value);
	return String(value);
}

function formatDateKey(value) {
	if (typeof value !== 'string') return value;
	if (isTimestampString(value)) return formatTimestamp(value);
	return value.replace('T', ' ').replace('.000000', '').replace('.000Z', '');
}

function getValueTitle(value) {
	if (value === null || value === undefined) return '';
	if (isObject(value)) return JSON.stringify(value, null, 2);
	return String(value);
}

function getSortValue(value) {
	if (value === null || value === undefined || value === '') return null;
	if (typeof value === 'number' || typeof value === 'boolean') return value;
	if (isTimestampString(value)) return getTimestampSortValue(value);
	if (typeof value === 'string' && Number.isFinite(Number(value))) return Number(value);
	return String(value).toLowerCase();
}

function flattenValue(value, prefix = '') {
	if (isPrimitive(value)) return [{ key: prefix || 'value', value }];
	if (Array.isArray(value)) {
		if (value.length === 0) return [{ key: prefix || 'value', value: [] }];

		return value.flatMap((entry, index) => flattenValue(entry, prefix ? prefix + '[' + index + ']' : '[' + index + ']'));
	}

	const keys = sortKeys(Object.keys(value));
	if (keys.length === 0) return [{ key: prefix || 'value', value: {} }];

	return keys.flatMap((key) => {
		const entry = value[key];
		const nextKey = prefix ? prefix + '.' + key : key;

		if (isPrimitive(entry)) return [{ key: nextKey, value: entry }];
		return flattenValue(entry, nextKey);
	});
}

function valuesEqual(a, b) {
	const aNumber = Number(a);
	const bNumber = Number(b);

	if (Number.isFinite(aNumber) && Number.isFinite(bNumber)) return Math.abs(aNumber - bNumber) < 1e-9;
	return JSON.stringify(a) === JSON.stringify(b);
}

function numericDiff(a, b) {
	const aNumber = Number(a);
	const bNumber = Number(b);

	if (!Number.isFinite(aNumber) || !Number.isFinite(bNumber)) return null;
	return bNumber - aNumber;
}

function getLeafKey(path) {
	const lastPart = String(path || '').split('.').pop() || '';
	return lastPart.replace(/\[\d+\]$/, '');
}

function findInjectMatch(key, injectRows) {
	const exactMatch = injectRows.find(row => row.key === key);
	if (exactMatch) return exactMatch;

	const leafKey = getLeafKey(key);
	return injectRows.find(row => getLeafKey(row.key) === leafKey) || null;
}

function getStatusClass(color, baseClassName = STATUS_BADGE_CLASS) {
	return baseClassName + ' ' + getSoftStatusClass(color);
}

function renderStatusBadge(label, color) {
	return <div className={getStatusClass(color, SMALL_BADGE_CLASS)}>{label}</div>;
}

function renderCompareStatus(row) {
	if (row.status === 'same') return renderStatusBadge('Same', 'green');
	if (row.status === 'changed') return renderStatusBadge('Changed', 'yellow');
	if (row.status === 'missing') return renderStatusBadge('Missing', 'red');
	return renderStatusBadge('-', 'gray');
}

function renderDiffCell(value) {
	if (!Number.isFinite(value)) {
		return <div className={getStatusClass('gray', STATUS_CELL_CLASS)}><span className='font-semibold'>-</span></div>;
	}

	if (value > 0) {
		return <div className={getStatusClass('green', STATUS_CELL_CLASS)}><span className='font-semibold'>+{formatNumber(value)}</span></div>;
	}

	if (value < 0) {
		return <div className={getStatusClass('yellow', STATUS_CELL_CLASS)}><span className='font-semibold'>{formatNumber(value)}</span></div>;
	}

	return <div className={getStatusClass('gray', STATUS_CELL_CLASS)}><span className='font-semibold'>0</span></div>;
}

function NameCell({ row }) {
	return (
		<div className='min-w-0'>
			<div className='text-gray-900 font-medium break-words'>{row.label || '-'}</div>
			{row.technicalName ? <div className='mt-2 text-10 text-gray-400 font-mono break-all'>{row.technicalName}</div> : null}
		</div>
	);
}

function ValueCell({ value }) {
	return (
		<Tooltip title={getValueTitle(value)}>
			<span className='font-mono break-all'>{formatValue(value)}</span>
		</Tooltip>
	);
}

function Tag({ label, color = 'gray' }) {
	return <span className={getStatusClass(color, SMALL_BADGE_CLASS)}>{label}</span>;
}

function SectionHeader({ title, technicalName, children }) {
	return (
		<div className='flex items-center gap-8 px-16 py-12'>
			<Typography className='font-semibold leading-none'>{title}</Typography>
			{technicalName ? <span className='text-10 text-gray-400 font-mono leading-none'>{technicalName}</span> : null}
			{children}
		</div>
	);
}

function PipelineTable({ tableConfig, rows, rowKey = 'id', minHeight = 260 }) {
	const hasRows = Array.isArray(rows) && rows.length > 0;

	return (
		<div className='w-full min-h-0' style={hasRows ? undefined : { minHeight }}>
			<OptimizationPipelinePopupTable
				tableConfig={tableConfig}
				rows={rows}
				rowKey={rowKey}
				minHeight={minHeight}
			/>
		</div>
	);
}

const keyValueTableConfig = {
	tableMinWidth: 620,
	sort: { key: 'label', direction: 'asc' },
	states: {
		emptyTitle: 'No data available'
	},
	columns: [
		{
			key: 'label',
			label: 'Field',
			sortable: true,
			minWidth: 240,
			renderCell: ({ row }) => <NameCell row={row} />
		},
		{
			key: 'value',
			label: 'Value',
			sortable: true,
			align: 'right',
			minWidth: 280,
			getSortValue: row => getSortValue(row.value),
			renderCell: ({ row }) => <ValueCell value={row.value} />
		}
	]
};

const objectValueTableConfig = {
	tableMinWidth: 720,
	sort: { key: 'label', direction: 'asc' },
	states: {
		emptyTitle: 'No values available'
	},
	columns: [
		{
			key: 'label',
			label: 'Name',
			sortable: true,
			minWidth: 320,
			renderCell: ({ row }) => <NameCell row={row} />
		},
		{
			key: 'value',
			label: 'Value',
			sortable: true,
			align: 'right',
			minWidth: 220,
			getSortValue: row => getSortValue(row.value),
			renderCell: ({ row }) => <ValueCell value={row.value} />
		}
	]
};

const domainTableConfig = {
	tableMinWidth: 900,
	sort: { key: 'label', direction: 'asc' },
	states: {
		emptyTitle: 'No domain data available'
	},
	columns: [
		{
			key: 'label',
			label: 'Domain',
			sortable: true,
			minWidth: 300,
			renderCell: ({ row }) => <NameCell row={row} />
		},
		{
			key: 'enabled',
			label: 'Enabled',
			sortable: true,
			align: 'center',
			minWidth: 110,
			renderCell: ({ row }) => formatValue(row.enabled)
		},
		{
			key: 'value',
			label: 'Value',
			sortable: true,
			align: 'right',
			minWidth: 120,
			getSortValue: row => getSortValue(row.value),
			renderCell: ({ row }) => <ValueCell value={row.value} />
		},
		{
			key: 'min',
			label: 'Min',
			sortable: true,
			align: 'right',
			minWidth: 120,
			getSortValue: row => getSortValue(row.min),
			renderCell: ({ row }) => <ValueCell value={row.min} />
		},
		{
			key: 'max',
			label: 'Max',
			sortable: true,
			align: 'right',
			minWidth: 120,
			getSortValue: row => getSortValue(row.max),
			renderCell: ({ row }) => <ValueCell value={row.max} />
		},
		{
			key: 'steps',
			label: 'Steps',
			sortable: true,
			align: 'right',
			minWidth: 100,
			getSortValue: row => getSortValue(row.steps),
			renderCell: ({ row }) => <ValueCell value={row.steps} />
		}
	]
};

const stateComparisonTableConfig = {
	tableMinWidth: 1050,
	sort: { key: 'label', direction: 'asc' },
	states: {
		emptyTitle: 'No comparable state values available'
	},
	columns: [
		{
			key: 'label',
			label: 'Attribute',
			sortable: true,
			minWidth: 300,
			renderCell: ({ row }) => <NameCell row={row} />
		},
		{
			key: 'stateValue',
			label: 'State',
			sortable: true,
			align: 'right',
			minWidth: 150,
			getSortValue: row => getSortValue(row.stateValue),
			renderCell: ({ row }) => <ValueCell value={row.stateValue} />
		},
		{
			key: 'injectValue',
			label: 'State inject',
			sortable: true,
			align: 'right',
			minWidth: 150,
			getSortValue: row => getSortValue(row.injectValue),
			renderCell: ({ row }) => <ValueCell value={row.injectValue} />
		},
		{
			key: 'diff',
			label: 'Inject - state',
			sortable: true,
			align: 'right',
			minWidth: 140,
			getSortValue: row => row.diff,
			renderCell: ({ row }) => renderDiffCell(row.diff)
		},
		{
			key: 'status',
			label: 'Status',
			sortable: true,
			align: 'center',
			minWidth: 120,
			renderCell: ({ row }) => renderCompareStatus(row)
		},
		{
			key: 'injectPath',
			label: 'Inject path',
			sortable: true,
			minWidth: 220,
			renderCell: ({ row }) => row.injectPath ? <span className='font-mono text-10 break-all'>{row.injectPath}</span> : '-'
		}
	]
};

const listTableConfig = {
	tableMinWidth: 640,
	sort: { key: 'index', direction: 'asc' },
	states: {
		emptyTitle: 'No list data available'
	},
	columns: [
		{
			key: 'index',
			label: '#',
			sortable: true,
			align: 'right',
			width: 90,
			renderCell: ({ row }) => row.index
		},
		{
			key: 'value',
			label: 'Value',
			sortable: true,
			minWidth: 500,
			getSortValue: row => getSortValue(row.value),
			renderCell: ({ row }) => <ValueCell value={row.value} />
		}
	]
};

function getTopLevelSummaryRows(data) {
	if (!isObject(data)) return [withLabel({ id: 'value', key: 'value', value: data })];

	const knownRows = topLevelSummaryKeys
		.filter((key) => Object.prototype.hasOwnProperty.call(data, key))
		.map((key) => withLabel({ id: key, key, value: data[key] }));

	const extraRows = sortKeys(Object.keys(data))
		.filter((key) => !topLevelSummaryKeys.includes(key) && key !== 'best_objective' && key !== 'domains' && key !== 'optimizer_details' && key !== 'meta' && !isDateKey(key))
		.map((key) => {
			const value = data[key];
			const displayValue = isObject(value) ? (Array.isArray(value) ? value.length + ' items' : Object.keys(value).length + ' keys') : value;
			return withLabel({ id: key, key, value: displayValue });
		});

	return [...knownRows, ...extraRows];
}

function getDateEntries(data) {
	if (!isObject(data) || Array.isArray(data)) return [];

	return sortKeys(Object.keys(data))
		.filter((key) => isDateKey(key))
		.map((key) => ({ date: key, value: data[key] }));
}

function getArrayEntries(value, prefix = '') {
	if (!isObject(value)) return [];

	if (Array.isArray(value)) {
		return [{ path: prefix || 'value', values: value }];
	}

	return Object.keys(value).flatMap((key) => {
		const nextPath = prefix ? prefix + '.' + key : key;
		return getArrayEntries(value[key], nextPath);
	});
}

function getObjectiveData(data) {
	const bestObjective = isObject(data) ? data.best_objective : null;
	if (isObject(bestObjective)) return bestObjective;
	if (isObject(data) && (isObject(data.state) || isObject(data.state_inject))) return data;
	return null;
}

function isModelValidationTask(item) {
	return String(item?.type || '').toLowerCase() === 'modelvalidation';
}

function isUnitOptimizerTask(item) {
	return String(item?.type || '').toLowerCase() === 'unitoptimizer';
}

function shouldSkipSummaryTab(item) {
	return isModelValidationTask(item) || isUnitOptimizerTask(item);
}

function getTableMinHeight(rowCount, rowHeight = 52, headerHeight = 56, maxHeight = 420) {
	return Math.min(maxHeight, headerHeight + Math.max(rowCount, 0) * rowHeight);
}

function buildTabs(data, item) {
	const tabs = shouldSkipSummaryTab(item) ? [] : [{ key: 'summary', label: 'Summary' }];
	const bestObjective = isObject(data) ? data.best_objective : null;
	const objectiveData = getObjectiveData(data);
	const hasBestObjective = isObject(bestObjective);
	const hasState = isObject(objectiveData?.state);
	const hasStateInject = isObject(objectiveData?.state_inject);
	const hasDomains = isObject(data?.domains) || isObject(bestObjective?.domain_inject);
	const dateEntries = getDateEntries(data);
	const arrayEntries = getArrayEntries(data).filter((entry) => entry.values.length > 0);

	if (hasBestObjective) {
		tabs.push({ key: 'bestObjective', label: 'Best' });
	}

	if (hasState && hasStateInject) {
		tabs.push({ key: 'stateCompare', label: 'State compare' });
	}

	if (hasState) {
		tabs.push({ key: 'state', label: 'State' });
	}

	if (hasStateInject) {
		tabs.push({ key: 'stateInject', label: 'State inject' });
	}

	if (hasBestObjective || hasDomains) tabs.push({ key: 'domains', label: 'Domains' });
	if (dateEntries.length > 0) tabs.push({ key: 'dateEntries', label: 'Date entries' });
	if (arrayEntries.length > 0) tabs.push({ key: 'lists', label: 'Lists' });

	tabs.push({ key: 'raw', label: 'Raw JSON' });

	return tabs;
}

function getBestObjectiveRows(bestObjective) {
	if (!isObject(bestObjective)) return [];

	const rows = bestObjectiveSummaryKeys
		.filter((key) => Object.prototype.hasOwnProperty.call(bestObjective, key))
		.map((key) => withLabel({ id: key, key, value: bestObjective[key] }));

	const extraRows = sortKeys(Object.keys(bestObjective))
		.filter((key) => !bestObjectiveSummaryKeys.includes(key) && !complexBestObjectiveKeys.includes(key))
		.map((key) => withLabel({ id: key, key, value: bestObjective[key] }));

	return [...rows, ...extraRows];
}

function getResultStatus(value) {
	if (!isObject(value)) return null;
	if (typeof value.successful === 'boolean') return value.successful;
	if (typeof value.is_successful === 'boolean') return value.is_successful;
	if (typeof value.accurate === 'boolean') return value.accurate;
	return null;
}

function getResultSummaryRows(value) {
	if (!isObject(value)) return [];

	const rows = [];
	['origin', 'message', 'successful', 'is_successful', 'accurate', 'error', 'error_message'].forEach((key) => {
		if (Object.prototype.hasOwnProperty.call(value, key)) rows.push(withLabel({ id: key, key, value: value[key] }));
	});

	if (isObject(value.details)) {
		Object.keys(value.details).forEach((key) => {
			if (isPrimitive(value.details[key])) rows.push(withLabel({ id: 'details.' + key, key: 'details.' + key, value: value.details[key] }));
		});
	}

	if (isObject(value.state)) rows.push(withLabel({ id: 'state', key: 'state', value: Object.keys(value.state).length + ' keys' }));
	if (isObject(value.completed_state)) rows.push(withLabel({ id: 'completed_state', key: 'completed_state', value: Object.keys(value.completed_state).length + ' keys' }));
	if (isObject(value.output_values)) rows.push(withLabel({ id: 'output_values', key: 'output_values', value: Object.keys(value.output_values).length + ' keys' }));

	return rows;
}

function isDomainObject(value) {
	return isObject(value) && (
		Object.prototype.hasOwnProperty.call(value, 'min_value') ||
		Object.prototype.hasOwnProperty.call(value, 'max_value') ||
		Object.prototype.hasOwnProperty.call(value, 'step_count') ||
		Object.prototype.hasOwnProperty.call(value, 'enabled')
	);
}

function getDomainRows(domains) {
	if (!isObject(domains)) return [];

	return sortKeys(Object.keys(domains)).map((key) => {
		const domain = domains[key];

		if (!isDomainObject(domain)) {
			return withLabel({
				id: key,
				key,
				name: key,
				enabled: null,
				value: domain,
				min: null,
				max: null,
				steps: null
			}, 'name');
		}

		return withLabel({
			id: key,
			key: domain.name || key,
			name: domain.name || key,
			enabled: domain.enabled,
			value: domain.value,
			min: domain.min_value,
			max: domain.max_value,
			steps: domain.step_count
		}, 'name');
	});
}

function getStateComparisonRows(state, stateInject) {
	const stateRows = isObject(state) ? flattenValue(state) : [];
	const injectRows = isObject(stateInject) ? flattenValue(stateInject) : [];

	return stateRows.map((stateRow) => {
		const match = findInjectMatch(stateRow.key, injectRows);
		const diff = match ? numericDiff(stateRow.value, match.value) : null;
		const status = !match ? 'missing' : valuesEqual(stateRow.value, match.value) ? 'same' : 'changed';

		return withLabel({
			id: stateRow.key,
			key: stateRow.key,
			stateValue: stateRow.value,
			injectValue: match?.value,
			injectPath: match?.key || '',
			diff,
			status
		});
	});
}

function getDateEntryKind(entries) {
	const first = entries.find((entry) => isObject(entry.value))?.value;
	if (!first) return 'generic';
	if (Object.prototype.hasOwnProperty.call(first, 'features') || Object.prototype.hasOwnProperty.call(first, 'accurate')) return 'validation';
	if (Object.prototype.hasOwnProperty.call(first, 'base_state') || Object.prototype.hasOwnProperty.call(first, 'optimum_state')) return 'unitOptimizer';
	return 'generic';
}

function getDateEntryRows(entries) {
	const kind = getDateEntryKind(entries);

	return entries.map((entry) => {
		const value = entry.value || {};
		const baseValue = value.base_value;
		const optimumValue = value.optimum_value;
		const delta = typeof baseValue === 'number' && typeof optimumValue === 'number' ? optimumValue - baseValue : null;

		return {
			id: entry.date,
			date: entry.date,
			dateLabel: formatDateKey(entry.date),
			kind,
			rawValue: value,
			accurate: value.accurate,
			successful: value.is_successful,
			mae: value.mae,
			mse: value.mse,
			mape: value.mape,
			maeAll: value.mae_all,
			mseAll: value.mse_all,
			mapeAll: value.mape_all,
			baseValue,
			optimumValue,
			delta,
			message: value.message,
			stateCount: countObjectKeys(value.state || value.base_state),
			completedStateCount: countObjectKeys(value.completed_state || value.optimum_state),
			featureCount: countObjectKeys(value.features),
			detailsValue: value.details?.value
		};
	});
}

function getDateEntryTableConfig(kind) {
	const baseColumns = [
		{
			key: 'dateLabel',
			label: 'Date',
			sortable: true,
			minWidth: 170,
			renderCell: ({ row }) => <span className='font-mono whitespace-nowrap'>{row.dateLabel}</span>
		}
	];

	if (kind === 'validation') {
		return {
			tableMinWidth: 1120,
			sort: { key: 'dateLabel', direction: 'asc' },
			states: { emptyTitle: 'No date entries available' },
			columns: [
				...baseColumns,
				{ key: 'accurate', label: 'Accurate', sortable: true, align: 'center', minWidth: 110, renderCell: ({ row }) => row.accurate === true ? renderStatusBadge('Yes', 'green') : row.accurate === false ? renderStatusBadge('No', 'red') : renderStatusBadge('-', 'gray') },
				{ key: 'mae', label: 'MAE', sortable: true, align: 'right', minWidth: 110, getSortValue: row => getSortValue(row.mae), renderCell: ({ row }) => <ValueCell value={row.mae} /> },
				{ key: 'mse', label: 'MSE', sortable: true, align: 'right', minWidth: 110, getSortValue: row => getSortValue(row.mse), renderCell: ({ row }) => <ValueCell value={row.mse} /> },
				{ key: 'mape', label: 'MAPE', sortable: true, align: 'right', minWidth: 110, getSortValue: row => getSortValue(row.mape), renderCell: ({ row }) => <ValueCell value={row.mape} /> },
				{ key: 'maeAll', label: 'MAE all', sortable: true, align: 'right', minWidth: 120, getSortValue: row => getSortValue(row.maeAll), renderCell: ({ row }) => <ValueCell value={row.maeAll} /> },
				{ key: 'mseAll', label: 'MSE all', sortable: true, align: 'right', minWidth: 120, getSortValue: row => getSortValue(row.mseAll), renderCell: ({ row }) => <ValueCell value={row.mseAll} /> },
				{ key: 'mapeAll', label: 'MAPE all', sortable: true, align: 'right', minWidth: 120, getSortValue: row => getSortValue(row.mapeAll), renderCell: ({ row }) => <ValueCell value={row.mapeAll} /> },
				{ key: 'featureCount', label: 'Features', sortable: true, align: 'right', minWidth: 105, renderCell: ({ row }) => row.featureCount },
				{ key: 'stateCount', label: 'State', sortable: true, align: 'right', minWidth: 90, renderCell: ({ row }) => row.stateCount },
				{ key: 'message', label: 'Message', sortable: true, minWidth: 260, renderCell: ({ row }) => <ValueCell value={row.message} /> }
			]
		};
	}

	if (kind === 'unitOptimizer') {
		return {
			tableMinWidth: 980,
			sort: { key: 'dateLabel', direction: 'asc' },
			states: { emptyTitle: 'No date entries available' },
			columns: [
				...baseColumns,
				{ key: 'successful', label: 'Successful', sortable: true, align: 'center', minWidth: 120, renderCell: ({ row }) => row.successful === true ? renderStatusBadge('Yes', 'green') : row.successful === false ? renderStatusBadge('No', 'red') : renderStatusBadge('-', 'gray') },
				{ key: 'baseValue', label: 'Base', sortable: true, align: 'right', minWidth: 130, getSortValue: row => getSortValue(row.baseValue), renderCell: ({ row }) => <ValueCell value={row.baseValue} /> },
				{ key: 'optimumValue', label: 'Optimum', sortable: true, align: 'right', minWidth: 130, getSortValue: row => getSortValue(row.optimumValue), renderCell: ({ row }) => <ValueCell value={row.optimumValue} /> },
				{ key: 'delta', label: 'Delta', sortable: true, align: 'right', minWidth: 120, getSortValue: row => getSortValue(row.delta), renderCell: ({ row }) => renderDiffCell(row.delta) },
				{ key: 'detailsValue', label: 'Details value', sortable: true, align: 'right', minWidth: 140, getSortValue: row => getSortValue(row.detailsValue), renderCell: ({ row }) => <ValueCell value={row.detailsValue} /> },
				{ key: 'stateCount', label: 'Base state', sortable: true, align: 'right', minWidth: 110, renderCell: ({ row }) => row.stateCount },
				{ key: 'completedStateCount', label: 'Optimum state', sortable: true, align: 'right', minWidth: 130, renderCell: ({ row }) => row.completedStateCount },
				{ key: 'message', label: 'Message', sortable: true, minWidth: 260, renderCell: ({ row }) => <ValueCell value={row.message} /> }
			]
		};
	}

	return {
		tableMinWidth: 620,
		sort: { key: 'dateLabel', direction: 'asc' },
		states: { emptyTitle: 'No date entries available' },
		columns: [
			...baseColumns,
			{ key: 'message', label: 'Summary', sortable: true, minWidth: 300, renderCell: ({ row }) => <ValueCell value={row.message} /> },
			{ key: 'rawValue', label: 'Keys', sortable: true, align: 'right', minWidth: 100, getSortValue: row => countObjectKeys(row.rawValue), renderCell: ({ row }) => countObjectKeys(row.rawValue) }
		]
	};
}

function KeyValueTable({ rows, emptyText }) {
	const visibleRows = (rows || []).filter(Boolean);

	if (visibleRows.length === 0) return <EmptyState>{emptyText || 'No data available.'}</EmptyState>;

	return <PipelineTable tableConfig={keyValueTableConfig} rows={visibleRows} rowKey='id' minHeight={getTableMinHeight(visibleRows.length, 54)} />;
}

function ObjectValueTable({ data, emptyText }) {
	const rows = useMemo(() => flattenValue(data).map(row => withLabel({ id: row.key, key: row.key, value: row.value })), [data]);

	if (!isObject(data) || isEmptyObject(data)) return <EmptyState>{emptyText || 'No data available.'}</EmptyState>;

	return <PipelineTable tableConfig={objectValueTableConfig} rows={rows} rowKey='id' minHeight={getTableMinHeight(rows.length, 54)} />;
}

function DomainTable({ domains }) {
	const rows = useMemo(() => getDomainRows(domains), [domains]);

	if (rows.length === 0) return <EmptyState>No domain data available.</EmptyState>;

	return <PipelineTable tableConfig={domainTableConfig} rows={rows} rowKey='id' minHeight={getTableMinHeight(rows.length, 54, 56, 300)} />;
}

function StateComparisonTable({ state, stateInject }) {
	const rows = useMemo(() => getStateComparisonRows(state, stateInject), [state, stateInject]);

	if (rows.length === 0) return <EmptyState>No state values available for comparison.</EmptyState>;

	return <PipelineTable tableConfig={stateComparisonTableConfig} rows={rows} rowKey='id' minHeight={getTableMinHeight(rows.length, 54)} />;
}

function ResultBlock({ title, data }) {
	if (!isObject(data)) return null;

	const status = getResultStatus(data);

	return (
		<div className='flex flex-col'>
			<SectionHeader title={title}>
				{status === true ? renderStatusBadge('Successful', 'green') : null}
				{status === false ? renderStatusBadge('Issue', 'red') : null}
			</SectionHeader>
			<KeyValueTable rows={getResultSummaryRows(data)} emptyText='No summary data available.' />
		</div>
	);
}

function DateEntriesTable({ entries }) {
	const rows = useMemo(() => getDateEntryRows(entries), [entries]);
	const kind = rows[0]?.kind || 'generic';
	const tableConfig = useMemo(() => getDateEntryTableConfig(kind), [kind]);

	if (rows.length === 0) return <EmptyState>No date entries available.</EmptyState>;

	return <PipelineTable tableConfig={tableConfig} rows={rows} rowKey='id' minHeight={getTableMinHeight(rows.length, 54)} />;
}

function ListsTab({ data }) {
	const entries = useMemo(() => getArrayEntries(data).filter((entry) => entry.values.length > 0), [data]);

	if (entries.length === 0) return <EmptyState>No list data available.</EmptyState>;

	return (
		<div className='flex flex-col'>
			{entries.map((entry) => {
				const rows = entry.values.map((value, index) => ({ id: entry.path + '-' + index, index: index + 1, value }));
				const title = inferName(entry.path);
				const technicalName = entry.path !== title ? entry.path : null;

				return (
					<div key={entry.path} className='flex flex-col'>
						<SectionHeader title={title} technicalName={technicalName}>
							<Tag label={entry.values.length + ' item' + (entry.values.length === 1 ? '' : 's')} />
						</SectionHeader>
						<PipelineTable tableConfig={listTableConfig} rows={rows} rowKey='id' minHeight={getTableMinHeight(rows.length, 48, 56, 360)} />
					</div>
				);
			})}
		</div>
	);
}

function EmptyState({ children }) {
	return <div className='w-full flex items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-gray-500 text-sm font-medium' style={{ minHeight: 180 }}>{children}</div>;
}

function RawJson({ data }) {
	return (
		<pre className='m-0 p-16 bg-grey-100 border overflow-auto text-xs leading-relaxed font-mono whitespace-pre-wrap break-all'>
			{JSON.stringify(data, null, 2)}
		</pre>
	);
}

function renderTabContent(tabKey, data) {
	const bestObjective = isObject(data) ? data.best_objective : null;
	const objectiveData = getObjectiveData(data);
	const dateEntries = getDateEntries(data);

	if (tabKey === 'summary') {
		return (
			<KeyValueTable rows={getTopLevelSummaryRows(data)} />
		);
	}

	if (tabKey === 'bestObjective') {
		return (
			<div className='flex flex-col'>
				<KeyValueTable rows={getBestObjectiveRows(bestObjective)} emptyText='No best objective summary available.' />
				<ResultBlock title='Aspect' data={bestObjective?.aspect} />
				<ResultBlock title='Transformer' data={bestObjective?.transformer} />
			</div>
		);
	}

	if (tabKey === 'stateCompare') return <StateComparisonTable state={objectiveData?.state} stateInject={objectiveData?.state_inject} />;

	if (tabKey === 'state') return <ObjectValueTable data={objectiveData?.state} emptyText='No state data available.' />;

	if (tabKey === 'stateInject') return <ObjectValueTable data={objectiveData?.state_inject} emptyText='No state inject data available.' />;

	if (tabKey === 'domains') {
		return (
			<DomainTable domains={data?.domains} />
		);
	}

	if (tabKey === 'dateEntries') return <DateEntriesTable entries={dateEntries} />;

	if (tabKey === 'lists') return <ListsTab data={data} />;

	return <RawJson data={data} />;
}

function TaskManagerLastResultDialog({ item, data, onClose }) {
	const tabs = useMemo(() => buildTabs(data, item), [data, item]);
	const [tabKey, setTabKey] = useState(tabs[0]?.key || 'summary');
	const selectedTab = tabs.some((tab) => tab.key === tabKey) ? tabKey : tabs[0]?.key;
	const title = 'Last Result of ' + (item?.name || '-');

	return (
		<Dialog open fullWidth maxWidth='lg' onClose={onClose}>
			<DialogTitle disableTypography className='border-b'>
				<div className='flex items-center justify-between gap-24'>
					<div className='min-w-0'>
						<div className='flex items-center gap-10 min-w-0'>
							<AssessmentIcon className='text-grey-700 flex-shrink-0' />
							<div className='leading-tight truncate font-semibold'>{title}</div>
						</div>
						{/* {item?.type ? (
							<Typography variant='caption' className='block text-grey-600 truncate pl-32'>
								{item.type}
							</Typography>
						) : null} */}
					</div>
					<Button onClick={onClose}>Close</Button>
				</div>
			</DialogTitle>
			<div className='border-b px-16 py-12 bg-white'>
				<div className='flex flex-wrap items-center gap-5'>
					<span className='text-11 leading-none opacity-70 mr-4 whitespace-nowrap'>View:</span>
					{tabs.map((tab) => (
						<PopupToolbarChip
							key={tab.key}
							label={tab.label}
							active={selectedTab === tab.key}
							onClick={() => setTabKey(tab.key)}
						/>
					))}
				</div>
			</div>
			<DialogContent className='p-0' style={{ maxHeight: '70vh', padding: 0 }}>
				{renderTabContent(selectedTab, data)}
			</DialogContent>
			<DialogActions className='border-t p-16'>
				<Button variant='contained' color='primary' onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
}

export default TaskManagerLastResultDialog;
